import React, { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography, Button, Chip, Divider, Stack, Skeleton } from '@mui/material';
import VerifiedIcon from '@mui/icons-material/Verified';
import InfoIcon from '@mui/icons-material/Info';
import { supabase } from '../../lib/supabase';

interface VerificationInfoModalProps {
  open: boolean;
  onClose: () => void;
  userId?: string | null;
}

interface VerificationRow {
  id: string;
  user_id: string;
  id_number: string | null;
  photo_url: string | null;
  storage_provider: string | null;
  created_at?: string;
}

const VerificationInfoModal: React.FC<VerificationInfoModalProps> = ({ open, onClose, userId }) => {
  const [loading, setLoading] = useState(false);
  const [row, setRow] = useState<VerificationRow | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [resolvedUrl, setResolvedUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !userId) return;
    const fetchInfo = async () => {
      setLoading(true); setError(null);
      try {
        const { data, error } = await supabase
          .from('verification_info')
          .select('*')
          .eq('user_id', userId)
          .order('created_at', { ascending: false })
          .limit(1);
        if (error) throw error;
        const latest = Array.isArray(data) && data.length > 0 ? (data[0] as any) : null;
        setRow(latest);
        setResolvedUrl(null);
        // If Backblaze, try to ensure a signed URL so the image can load
        if (latest?.storage_provider === 'backblaze' && latest?.photo_url) {
          let objectKey: string | null = null;
          const val: string = latest.photo_url;
          if (/^https?:\/\//i.test(val)) {
            try {
              const u = new URL(val);
              const parts = u.pathname.split('/').filter(Boolean);
              // Expect /<bucket>/<objectKey...>
              if (parts.length >= 2) {
                objectKey = decodeURIComponent(parts.slice(1).join('/'));
              }
            } catch {}
          } else {
            objectKey = val; // already an objectKey
          }
          if (objectKey) {
            try {
              const { data: { session } } = await supabase.auth.getSession();
              const resp = await fetch('/api/b2-list.php', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
                },
                body: JSON.stringify({ signRead: true, objectKey })
              });
              const json = await resp.json();
              if (resp.ok && json?.ok && Array.isArray(json.items) && json.items[0]?.url) {
                setResolvedUrl(json.items[0].url as string);
              } else if (/^https?:\/\//i.test(val)) {
                setResolvedUrl(val);
              }
            } catch {}
          }
        }
      } catch (e: any) {
        setError(e?.message || 'Fehler beim Laden der Verifizierungsdaten');
      } finally {
        setLoading(false);
      }
    };
    fetchInfo();
  }, [open, userId]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center">
          <VerifiedIcon color="primary" />
          <Typography variant="h6">Verifizierungsdetails</Typography>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <>
            <Skeleton variant="rectangular" height={220} sx={{ mb: 2, borderRadius: 2 }} />
            <Skeleton variant="text" width="60%" />
            <Skeleton variant="text" width="40%" />
          </>
        ) : error ? (
          <Chip icon={<InfoIcon />} color="error" label={error} />
        ) : !row ? (
          <Chip icon={<InfoIcon />} color="warning" label="Keine Verifizierungsdaten gefunden." />
        ) : (
          <Box sx={{ display: 'grid', gap: 2 }}>
            {row.photo_url && (
              <Box sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid rgba(0,0,0,0.08)' }}>
                <img src={resolvedUrl || row.photo_url} alt="Verifizierungsfoto" style={{ width: '100%', display: 'block' }} />
              </Box>
            )}
            <Divider />
            <Stack direction="row" spacing={2} flexWrap="wrap">
              <Chip color="success" icon={<VerifiedIcon />} label="Verifiziert" />
              {row.storage_provider && (
                <Chip label={`Storage: ${row.storage_provider}`} />
              )}
            </Stack>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Ausweis-/ID-Nummer</Typography>
              <Typography variant="body1">{row.id_number || '—'}</Typography>
            </Box>
            {row.created_at && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Zeitpunkt</Typography>
                <Typography variant="body1">{new Date(row.created_at).toLocaleString()}</Typography>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">Schließen</Button>
      </DialogActions>
    </Dialog>
  );
};

export default VerificationInfoModal;
